import React, { useMemo } from 'react';
import { View } from 'react-native';
import { Checkbox, makeStyles } from '@material-ui/core';
import { createGlobalStyle } from 'styled-components';
import TableContainer from '../../../../components/web/ReactTable/Table.container';
import SearchInputContainer from '../../../../components/shared/SearchInput/SearchInput.container';
import colors from '../../../../config/colors';

const TableStyle = createGlobalStyle`
  .checkbox-table thead th {
    background-color: #282C6F !important;
    color: white !important;
  }
  .checkbox-table thead .MuiCheckbox-root {
    color: white !important;
  }
  .checkbox-table th,
  .checkbox-table td {
    border-color: #E2EDF4 !important;
  }
  .checkbox-table * {
    scrollbar-width: none;
    -ms-overflow-style: none;
  }
  .checkbox-table *::-webkit-scrollbar {
    display: none;
  }
`;

interface ColumnDef {
	accessor: string;
	Header: string;
	isSortable?: boolean;
	CustomCell?: (cell: any) => React.ReactNode;
}

interface Props {
	data: any[];
	columns: ColumnDef[];
	keyName: string;
	selectedIds: Array<string | number>;
	onSelectionChange: (ids: Array<string | number>) => void;
	searchPlaceholder: string;
	onSearch: (query: string) => void;
	height?: number;
	width?: string | number;
	getNextData?: () => void;
	allDataFetched?: boolean;
}

const useStyles = makeStyles(() => ({
	checkbox: {
		color: '#6d7278',
		padding: 4,
		'&$checked': {
			color: colors.titlePrimary,
		},
		'&$indeterminate': {
			color: colors.titlePrimary,
		},
	},
	checked: {},
	indeterminate: {},
}));

const CheckboxTable = ({
	data,
	columns,
	keyName,
	selectedIds,
	onSelectionChange,
	searchPlaceholder,
	onSearch,
	height,
	width = '48%',
	getNextData,
	allDataFetched = true,
}: Props) => {
	const classes = useStyles();

	const allSelected =
		data.length > 0 && data.every(d => selectedIds.includes(d[keyName]));
	const someSelected =
		data.some(d => selectedIds.includes(d[keyName])) && !allSelected;

	const toggleAll = (checked: boolean) => {
		if (checked) {
			const visibleIds = data.map(d => d[keyName]);
			onSelectionChange(Array.from(new Set([...selectedIds, ...visibleIds])));
		} else {
			const visibleSet = new Set(data.map(d => d[keyName]));
			onSelectionChange(selectedIds.filter(id => !visibleSet.has(id)));
		}
	};

	const toggleOne = (id: string | number, checked: boolean) => {
		if (checked) {
			if (!selectedIds.includes(id)) onSelectionChange([...selectedIds, id]);
		} else {
			onSelectionChange(selectedIds.filter(v => v !== id));
		}
	};

	const tableHeaders = useMemo(
		() => [
			{
				accessor: '__select',
				isSortable: false,
				style: { width: 30 },
				Header: (
					<Checkbox
						size='small'
						classes={{
							root: classes.checkbox,
							checked: classes.checked,
							indeterminate: classes.indeterminate,
						}}
						color='default'
						checked={allSelected}
						indeterminate={someSelected}
						onChange={e => toggleAll(e.target.checked)}
					/>
				),
				CustomCell: ({ row }: any) => (
					<Checkbox
						size='small'
						classes={{
							root: classes.checkbox,
							checked: classes.checked,
						}}
						color='default'
						checked={selectedIds.includes(row.original[keyName])}
						onChange={e =>
							toggleOne(row.original[keyName], e.target.checked)
						}
					/>
				),
			},
			...columns,
		],
		[columns, selectedIds, data, allSelected, someSelected],
	);

	return (
		<View style={{ width, height: '100%' }}>
			<TableStyle />
			<View style={{ marginBottom: 8 }}>
				<SearchInputContainer
					placeholder={searchPlaceholder}
					config={{ name: 'search', type: 'searchInput' }}
					onChange={({ value }: any) => onSearch(value)}
					styleConf={{
						container: { marginBottom: 0, borderRadius: 8, borderColor: colors.clockGreyText, borderWidth: 1, overflow: 'hidden' },
						input: { borderRadius: 8, outlineStyle: 'none', outlineWidth: 0 },
					}}
				/>
			</View>
			<div
				className='checkbox-table'
				style={{ border: '1px solid #E2EDF4', borderRadius: 8, overflow: 'hidden' }}
			>
				<TableContainer
					keyName={keyName}
					headers={tableHeaders as any}
					data={data}
					getNextData={getNextData || (() => {})}
					allDataFetched={allDataFetched}
					noInfinityScroll={!getNextData}
					hasFooter={false}
					onSort={() => {}}
					getUpdatedDataFromTable={() => {}}
					windowScale={0}
					height={height || 360}
				/>
			</div>
		</View>
	);
};

export default CheckboxTable;
